import { PasswordInput, Progress, Text } from '@mantine/core';
import { PASSWORD_MIN_LENGTH, PASSWORD_MIN_SCORE } from '@twinlink/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PasswordCheck {
  valid: boolean;
  score: number;
}

/**
 * New-password input with the live policy check (doc 02 §7.2): 12+ characters, zxcvbn score
 * of at least 3, different from the email. The server re-checks; this is for inline feedback.
 */
export function NewPasswordField({
  value,
  onChange,
  onCheck,
  email,
  label,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  onCheck: (check: PasswordCheck) => void;
  email?: string;
  label?: string;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const [score, setScore] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setScore(0);
      onCheck({ valid: false, score: 0 });
      return;
    }
    // zxcvbn is large: it is loaded only on screens that set a password.
    void import('zxcvbn').then(({ default: zxcvbn }) => {
      if (cancelled) return;
      const hints = [email ?? '', (email ?? '').split('@')[0] ?? '', 'twinlink', 'smardac'].filter(Boolean);
      const s = zxcvbn(value.slice(0, 100), hints).score;
      setScore(s);
      const equalsEmail = !!email && value.trim().toLowerCase() === email.toLowerCase();
      onCheck({ valid: value.length >= PASSWORD_MIN_LENGTH && s >= PASSWORD_MIN_SCORE && !equalsEmail, score: s });
    });
    return () => {
      cancelled = true;
    };
    // onCheck is intentionally not a dependency: callers pass an inline setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, email]);

  const tooShort = value.length > 0 && value.length < PASSWORD_MIN_LENGTH;
  const weak = value.length >= PASSWORD_MIN_LENGTH && score < PASSWORD_MIN_SCORE;
  const hint = tooShort
    ? t('auth.password.tooShort', { count: PASSWORD_MIN_LENGTH })
    : weak
      ? t('auth.password.tooWeak')
      : value
        ? t('auth.password.strong')
        : t('auth.password.rule', { count: PASSWORD_MIN_LENGTH });
  const color = !value ? 'gray' : tooShort || weak ? 'orange' : 'green';

  return (
    <div>
      <PasswordInput
        label={label ?? t('auth.password.new')}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        autoComplete="new-password"
        autoFocus={autoFocus}
        required
        aria-describedby="new-password-hint"
      />
      <Progress value={value ? Math.max(10, ((score + 1) / 5) * 100) : 0} color={color} size={4} mt={8} aria-hidden />
      <Text id="new-password-hint" size="xs" c={color === 'orange' ? 'orange.8' : 'dimmed'} mt={4} role="status">
        {hint}
      </Text>
    </div>
  );
}
